import React from "react";
import { TopBar } from "@/components/TopBar";
import { apiWaStatus, apiWaPesan, apiWaUlang, apiWaProses, apiGetPengaturan, apiUpdatePengaturan, formatApiErrorDetail } from "@/lib/api";
import { Loader2, RefreshCw, Send, Save } from "lucide-react";
import * as W from "@/lib/waktu";

// WhatsApp (F1.5-L, owner): outbox log, resend, manual wa.me, templates.
// Mode and token live on the server (env); the page only shows whether a
// token is set, never the token.
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const inputCls = "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const KEJADIAN = [["ORDER_BARU", "Order baru"], ["PEMBAYARAN", "Pembayaran diterima"], ["ACC_LINK", "Link ACC desain"], ["SIAP_DIAMBIL", "Siap diambil"],
  ["PENGINGAT_TAGIHAN", "Pengingat tagihan"],  // owner 2026-10-15 (A8): sent by Kasir/CS from the reminder list
  ["DIAMBIL", "Sesudah diambil (ajakan menilai)"],  // owner 2026-10-16: only while customer ratings are on
  ["ACC_REVISI", "Link ACC revisi selesai"]];  // v2.19 (owner 2026-10-26): "Kirim link ACC via WA" after a finished revision
const PLACEHOLDER = ["nama_customer", "no_faktur", "jenis_order", "tagihan", "diskon", "grand_total", "dibayar", "sisa", "status_bayar",
  "link_nota", "link_customer", "link_acc", "rekening", "rencana_selesai", "batas_nilai", "revisi_ke"];
const CLS = { TERKIRIM: "text-emerald-300", SIMULASI: "text-sky-300", ANTRE: "text-slate-300", MENGIRIM: "text-slate-300", GAGAL: "text-red-300" };

function Template() {
  const [cfg, setCfg] = React.useState(null);
  const [pilih, setPilih] = React.useState("ORDER_BARU");
  const [msg, setMsg] = React.useState(null);
  React.useEffect(() => { apiGetPengaturan().then(setCfg).catch((e) => setMsg(errText(e))); }, []);
  if (!cfg) return msg ? <div className="text-sm text-red-300">{msg}</div> : null;
  const simpan = async () => {
    setMsg(null);
    try {
      const r = await apiUpdatePengaturan({ wa_template: cfg.wa_template, wa_kejadian_aktif: cfg.wa_kejadian_aktif, wa_jeda_detik: parseInt(cfg.wa_jeda_detik, 10) || 5,
        pengingat_tagihan_hari: parseInt(cfg.pengingat_tagihan_hari, 10) || 2, pengingat_tagihan_maks: parseInt(cfg.pengingat_tagihan_maks, 10) || 2,
        ...(cfg.pengingat_tagihan_mulai ? { pengingat_tagihan_mulai: cfg.pengingat_tagihan_mulai } : {}) });
      setCfg(r); setMsg("Template tersimpan");
    } catch (e) { setMsg(errText(e)); }
  };
  return (
    <div data-testid="wa-template" className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-3">
      <h2 className="font-semibold text-slate-100">Template pesan</h2>
      <div className="flex flex-wrap gap-2">
        {KEJADIAN.map(([k, l]) => (
          <button key={k} onClick={() => setPilih(k)} className={`rounded-md px-3 py-1.5 text-sm border ${pilih === k ? "border-amber-500 bg-amber-500 text-slate-950 font-semibold" : "border-slate-700 text-slate-400"}`}>
            {l}{cfg.wa_kejadian_aktif[k] ? "" : " (mati)"}</button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input data-testid="wa-aktif" type="checkbox" checked={!!cfg.wa_kejadian_aktif[pilih]} onChange={(e) => setCfg({ ...cfg, wa_kejadian_aktif: { ...cfg.wa_kejadian_aktif, [pilih]: e.target.checked } })} />
        {pilih === "PENGINGAT_TAGIHAN" ? "Pengingat tagihan aktif (kasir mengirim dari daftar \"Jadwal pengingat hari ini\" di menu Pembayaran)"
          : pilih === "DIAMBIL" ? "Kirim otomatis sesudah pesanan diambil/dikirim (hanya bila Penilaian customer aktif; {batas_nilai} = batas tanggal menilai)"
          : "Kirim otomatis untuk kejadian ini"}</label>
      {pilih === "PENGINGAT_TAGIHAN" && (
        <div data-testid="wa-pengingat-atur" className="flex flex-wrap items-center gap-3 text-sm text-slate-300">
          <label>Setiap <input data-testid="wa-pengingat-hari" type="number" min="1" max="30" value={cfg.pengingat_tagihan_hari}
            onChange={(e) => setCfg({ ...cfg, pengingat_tagihan_hari: e.target.value })} className={`${inputCls} w-16 mx-1`} /> hari</label>
          <label>maksimal <input data-testid="wa-pengingat-maks" type="number" min="1" max="10" value={cfg.pengingat_tagihan_maks}
            onChange={(e) => setCfg({ ...cfg, pengingat_tagihan_maks: e.target.value })} className={`${inputCls} w-16 mx-1`} /> kali per faktur</label>
          <label>untuk faktur sejak <input data-testid="wa-pengingat-mulai" type="date" value={cfg.pengingat_tagihan_mulai || ""}
            onChange={(e) => setCfg({ ...cfg, pengingat_tagihan_mulai: e.target.value })} className={`${inputCls} mx-1`} /></label>
          <span className="text-xs text-slate-500">Berhenti otomatis saat faktur lunas. Faktur sebelum tanggal itu ditagih manual.</span>
        </div>
      )}
      <textarea data-testid="wa-template-isi" rows={14} value={cfg.wa_template[pilih]} onChange={(e) => setCfg({ ...cfg, wa_template: { ...cfg.wa_template, [pilih]: e.target.value } })}
        className={`${inputCls} w-full font-mono text-xs`} />
      <div className="text-xs text-slate-500">Placeholder: {PLACEHOLDER.map((p) => <code key={p} className="mr-2 text-slate-400">{`{${p}}`}</code>)} — placeholder lain ditolak saat simpan. *teks* = tebal di WhatsApp.</div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-300">Jeda antar pesan <input type="number" min="1" max="300" value={cfg.wa_jeda_detik} onChange={(e) => setCfg({ ...cfg, wa_jeda_detik: e.target.value })} className={`${inputCls} w-20 ml-1`} /> detik</label>
        <button data-testid="wa-template-simpan" onClick={simpan} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950"><Save size={14} /> Simpan</button>
        {msg && <span data-testid="wa-template-msg" className="text-sm text-slate-300">{msg}</span>}
      </div>
    </div>
  );
}

export default function WhatsappPage() {
  const [st, setSt] = React.useState(null);
  const [rows, setRows] = React.useState(null);
  const [f, setF] = React.useState({ status: "", q: "" });
  const [buka, setBuka] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const load = React.useCallback(async () => {
    try {
      const [s, p] = await Promise.all([apiWaStatus(), apiWaPesan(Object.fromEntries(Object.entries(f).filter(([, v]) => v)))]);
      setSt(s); setRows(p);
    } catch (e) { setMsg(errText(e)); }
  }, [f]);
  React.useEffect(() => { load(); }, [load]);
  const proses = async () => {
    setBusy(true); setMsg(null);
    try { const r = await apiWaProses(); setMsg(`${r.diproses} pesan diproses`); await load(); } catch (e) { setMsg(errText(e)); } finally { setBusy(false); }
  };
  const ulang = async (p) => {
    try { await apiWaUlang(p.id); setMsg("Pesan dimasukkan ke antrean lagi"); load(); } catch (e) { setMsg(errText(e)); }
  };
  return (
    <div data-testid="whatsapp-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">WhatsApp</h1>
          <p className="text-sm text-slate-400 mt-1">Pesan otomatis ke customer: order baru, pembayaran diterima, link ACC desain, siap diambil, pengingat tagihan. Gagal kirim tidak pernah membatalkan transaksi.</p>
        </div>
        {st && (
          <div data-testid="wa-mode" className={`rounded-lg border p-3 text-sm ${st.mode === "fonnte" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-sky-500/40 bg-sky-500/10 text-sky-200"}`}>
            Mode: <b>{st.mode === "fonnte" ? "Fonnte (dikirim sungguhan)" : "SIMULASI — pesan hanya dicatat, tidak dikirim"}</b>
            {st.mode === "fonnte" && !st.token_diatur && <span className="text-red-300"> · token Fonnte belum diatur di server</span>}
            <span className="ml-3">Antre: {st.antre} · Gagal: {st.gagal}</span>
            <span className="block text-xs opacity-80">Mode dan token diatur di server (WA_MODE, FONNTE_TOKEN), bukan di aplikasi.</span>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls}>
            <option value="">Semua status</option>{["ANTRE", "MENGIRIM", "TERKIRIM", "SIMULASI", "GAGAL"].map((s) => <option key={s} value={s}>{s}</option>)}</select>
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="No. faktur" className={inputCls} />
          <button onClick={load} className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-300"><RefreshCw size={14} /> Muat ulang</button>
          <button data-testid="wa-proses" disabled={busy} onClick={proses} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Kirim antrean sekarang</button>
          {msg && <span data-testid="wa-msg" className="text-sm text-slate-300">{msg}</span>}
        </div>
        <div className="space-y-1">
          {rows?.length === 0 && <p className="text-slate-500">Belum ada pesan.</p>}
          {rows?.map((p) => (
            <div key={p.id} data-testid={`wa-row-${p.id}`} className="rounded-lg border border-slate-800 bg-slate-900/60 p-2.5 text-sm">
              <div className="flex flex-wrap items-center gap-2 cursor-pointer" onClick={() => setBuka(buka === p.id ? null : p.id)}>
                <span className={`font-mono text-xs ${CLS[p.status]}`}>{p.status}</span>
                <span className="text-xs rounded border border-slate-700 px-1.5">{p.kejadian}</span>
                <span className="font-mono text-amber-300">{p.nomor_faktur}</span>
                <span className="text-slate-400">{p.tujuan || "nomor tidak valid"}</span>
                <span className="text-xs text-slate-500">{W.waktu(p.dibuat)} · percobaan {p.percobaan}{p.ulang_dari ? " · kirim ulang" : ""}</span>
                {p.error && <span className="text-xs text-red-300">{p.error}</span>}
              </div>
              {buka === p.id && (
                <div className="mt-2 space-y-2">
                  <pre className="whitespace-pre-wrap rounded bg-slate-950 p-2 text-xs text-slate-300">{p.isi}</pre>
                  <div className="flex gap-3 text-xs">
                    <button onClick={() => ulang(p)} className="text-amber-300 hover:underline">Kirim ulang (antrean)</button>
                    {p.wa_me && <a href={p.wa_me} target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:underline">Kirim manual (wa.me)</a>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <Template />
      </main>
    </div>
  );
}
